/**
 * MBA Lab — the Change and AI Readiness Assessment's 12 dimensions. Pure
 * data, no functions: readiness-model.js reads this and the Razor view never
 * hardcodes dimension copy, matching the planner-modes.js precedent of
 * keeping every piece of editorial copy in one place a copy-editor can find
 * without touching the state-machine logic in readiness-model.js. Exactly
 * the plan's list (stakeholders through communication credibility).
 */

export const DIMENSIONS = Object.freeze([
  {
    key: 'humanOversight',
    label: 'Human oversight',
    question: 'Is there a clear point where a human reviews or can override this, rather than it running fully automated?',
    riskText: 'Without a defined human checkpoint, a wrong or harmful output can reach someone with no one positioned to catch it first.',
    progressText: 'Confirm exactly who has override authority and at exactly which step - "someone reviews it" is not the same as a named person at a named point.',
    addressedText: 'A real human checkpoint exists. Revisit it if the tool\'s scope expands beyond what that checkpoint was designed to review.',
  },
  {
    key: 'qualityRiskControls',
    label: 'Quality & risk controls',
    question: 'Is there a way to catch and correct a wrong or harmful output before it reaches someone who relies on it?',
    riskText: 'Without a catch mechanism, errors reach real decisions before anyone notices - the cost of a mistake compounds the longer it goes undetected.',
    progressText: 'Confirm the catch mechanism actually covers the highest-stakes outputs, not just the easiest ones to check.',
    addressedText: 'A real quality control exists. Keep testing it against edge cases, not just the typical case it was designed around.',
  },
  {
    key: 'harmReportingChannels',
    label: 'Channels for raising harm',
    question: 'If this causes a real problem for someone, do they have an actual, known way to raise it?',
    riskText: 'Without a known reporting channel, problems get absorbed silently instead of surfaced - you lose the signal that something needs to change.',
    progressText: 'Confirm people actually know the channel exists and trust that raising something won\'t be held against them.',
    addressedText: 'A real channel exists and is known. Periodically confirm it\'s still being used, not just still technically available.',
  },
  {
    key: 'dataGovernance',
    label: 'Data governance',
    question: 'Is it clear what data this touches, who can see it, and where it\'s allowed to go?',
    riskText: 'Without clear data boundaries, sensitive information can end up somewhere it was never meant to go, often before anyone notices.',
    progressText: 'Confirm the boundaries are actually documented and enforced technically, not just agreed to verbally.',
    addressedText: 'Data boundaries are documented. Revisit them whenever the tool\'s scope or data sources change.',
  },
  {
    key: 'jobLossIdentity',
    label: 'Job-loss & identity concerns',
    question: 'Have you addressed, honestly, whether this raises real job-security or role-identity concerns for anyone - and if so, how?',
    riskText: 'Unaddressed job-security fear doesn\'t go away quietly - it usually resurfaces as quiet resistance, disengagement, or people protecting information instead of sharing it.',
    progressText: 'Confirm this has actually been named out loud with the people affected, not just handled through a general reassurance in an all-hands.',
    addressedText: 'This has been named directly. Keep checking in - a one-time reassurance rarely fully resolves an ongoing fear.',
  },
  {
    key: 'stakeholders',
    label: 'Stakeholders & affected roles',
    question: 'Have you identified everyone whose work actually changes because of this - not just the team piloting it?',
    riskText: 'Missing an affected group means their concerns surface late, after the rollout is already underway and harder to adjust.',
    progressText: 'Double-check for people who are affected indirectly - downstream teams, support functions, anyone whose input the tool touches.',
    addressedText: 'The affected group is mapped. Revisit it if the rollout expands to a new team or use case.',
  },
  {
    key: 'businessCase',
    label: 'Business case',
    question: 'Is there a clear, specific reason this change is happening, distinct from "everyone else is doing it"?',
    riskText: 'Without a specific reason, it\'s hard to evaluate whether the change is actually working, and hard to explain credibly to the people affected by it.',
    progressText: 'Confirm the reason is specific enough that you could tell, concretely, if it turned out to be wrong.',
    addressedText: 'A specific business case exists. Keep it visible - it\'s also what makes the change explainable to the people living through it.',
  },
  {
    key: 'workflowFit',
    label: 'Workflow fit',
    question: 'Have you actually walked through how this fits, or doesn\'t, into the real current workflow - not the ideal one?',
    riskText: 'A tool that doesn\'t fit the real workflow creates extra work-arounds rather than saving time, and that gap is usually invisible until people are already living with it.',
    progressText: 'Confirm you\'ve walked the actual, current process end-to-end, including the messy exceptions, not just the clean common case.',
    addressedText: 'Workflow fit has been walked through directly. Recheck it if the underlying process changes.',
  },
  {
    key: 'skillsTraining',
    label: 'Skills & training',
    question: 'Is there a real plan for how affected people build the skill to use this well, not just a one-time announcement?',
    riskText: 'Without real training, adoption depends entirely on individual initiative - usage stays shallow and inconsistent, and the people who need it most may adopt it least.',
    progressText: 'Confirm the plan covers ongoing skill-building, not just a single launch-day session nobody can revisit.',
    addressedText: 'A real training plan exists. Keep checking whether it\'s reaching people who joined or changed roles after the initial rollout.',
  },
  {
    key: 'pilotDesign',
    label: 'Pilot design',
    question: 'Is there a bounded way to test this with a smaller group before it\'s the default for everyone?',
    riskText: 'Skipping a bounded pilot means problems get discovered at full scale, where they\'re more expensive and more visible to fix.',
    progressText: 'Confirm the pilot group is genuinely representative, not just the most enthusiastic early adopters.',
    addressedText: 'A real pilot exists. Make sure its lessons are actually being fed back into the full rollout, not just documented and set aside.',
  },
  {
    key: 'adoptionMetrics',
    label: 'Adoption metrics',
    question: 'Do you know, concretely, how you\'ll tell whether this is actually working, beyond "people are using it"?',
    riskText: 'Without a real definition of success, a rollout can coast indefinitely without anyone being able to say whether it\'s actually delivering value.',
    progressText: 'Confirm the metric measures outcome, not just activity - usage counts can rise even while the underlying problem stays unsolved.',
    addressedText: 'A real outcome metric exists. Keep revisiting whether it still reflects what actually matters as the rollout matures.',
  },
  {
    key: 'communicationCredibility',
    label: 'Communication credibility',
    question: 'Has the reasoning for this change been explained honestly, including its real tradeoffs and uncertainties - not just the upside?',
    riskText: 'One-sided communication tends to be quietly discounted - people notice when the tradeoffs are missing, and it costs credibility for the next change too.',
    progressText: 'Confirm the communication has actually named a real tradeoff or uncertainty, not just benefits.',
    addressedText: 'The communication has been honest about tradeoffs. That credibility carries forward - keep it consistent for what comes next.',
  },
]);

// Fixed priority order for the action plan - safety/harm-adjacent dimensions
// surface first (matches the plan's workplace-safety-first guardrail), not a
// numeric weighting of any kind. This order is data, not a computed score.
// Exported (only) so a test can pin its key set against DIMENSIONS's - a key
// present in one but not the other makes buildReadout()'s priorityRank.get()
// return undefined for that dimension, and undefined - number is NaN; sort's
// comparator contract coerces a NaN result to "equal" rather than throwing,
// so a missing/renamed key here silently breaks the safety-first ordering
// instead of erroring.
export const PRIORITY_ORDER = [
  'humanOversight',
  'qualityRiskControls',
  'harmReportingChannels',
  'dataGovernance',
  'jobLossIdentity',
  'stakeholders',
  'businessCase',
  'workflowFit',
  'skillsTraining',
  'pilotDesign',
  'adoptionMetrics',
  'communicationCredibility',
];
